import { Dispatch, useContext, useEffect } from "react";
import {
  ClaimCheckAction,
  ClaimCheckActionType,
  ClaimCheckState,
  initialState,
} from "@/core/contexts/ClaimCheckContext/reducer";
import {
  ClaimCheckContext,
  ClaimCheckDispatchContext,
} from "@/core/contexts/ClaimCheckContext/ClaimCheckContext";
import { camundaApi } from "@/core/api/camundaApi";
import { InitialisationConfig } from "@/core/utils/getInitialisationConfig";
import { ActionContent } from "@/core/components/Action/actions";
import { ConfigContext } from "@/core/contexts/ConfigContext";
import { TaskData } from "@/core/types/types";
import trackPageViewEvents, {
  trackCCStartedEvent,
} from "@/core/utils/trackPageViewEvents";
import DeviceDetector from "device-detector-js";

interface UseClaimCheckReturnValue extends ClaimCheckState {
  initialise: (config?: InitialisationConfig) => Promise<void>;
  submitStep: (formAnswers: {}) => Promise<void>;
  goToPreviousStep: () => Promise<void>;
  refreshStep: () => Promise<void>;
  reset: () => Promise<void>;
  flush: () => Promise<void>;
  executeAction: (
    content: ActionContent,
    process: string,
    utm_campaign?: string
  ) => Promise<void>;
  syncSignature: (process: string) => Promise<void>;
}

export const useClaimCheck = (): UseClaimCheckReturnValue => {
  const { currentStep, isLoading, error, visitedSteps } =
    useContext(ClaimCheckContext) || initialState;
  const currentConfig = useContext(ConfigContext);

  // const router = useRouter();

  const dispatch: Dispatch<ClaimCheckAction> = useContext(
    ClaimCheckDispatchContext
  ) as Dispatch<ClaimCheckAction>;

  const pushVisitedStep = (stepId: string) => {
    dispatch({ type: ClaimCheckActionType.PushVisitedStep, payload: stepId });
  };

  const popVisitedStep = () => {
    dispatch({ type: ClaimCheckActionType.PopVisitedStep });
  };

  const setCurrentStep = (payload: TaskData | any) => {
    const stepHash =
      payload.extensionProperties?.displayStepTitle || "next-step";
    history.pushState({}, "", `#${stepHash}`);
    dispatch({ type: ClaimCheckActionType.SetCurrentStep, payload });
  };
  const setError = (error: Error | null) => {
    dispatch({
      type: ClaimCheckActionType.SetError,
      payload: error,
    });
  };

  const setLoading = (isLoading: boolean) => {
    dispatch({
      type: ClaimCheckActionType.SetIsLoading,
      payload: isLoading,
    });
  };

  const initialise = async (config?: InitialisationConfig) => {
    setLoading(true);

    try {
      const currentStep = await camundaApi.init(config ?? currentConfig);
      pushVisitedStep(
        currentStep.extensionProperties?.displayStepTitle || "next-step"
      );
      setCurrentStep(currentStep);
      waitForConsentAndPush();
    } catch (error: any) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const submitStep = async (formAnswers: {}) => {
    setLoading(true);

    try {
      const { taskData: currentStep } = await camundaApi.submit(formAnswers);
      pushVisitedStep(currentStep.extensionProperties?.displayStepTitle);
      setCurrentStep(currentStep);
    } catch (error: any) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousStep = async () => {
    setLoading(true);

    try {
      const { taskData: currentStep } = await camundaApi.goToPreviousStep();
      popVisitedStep();
      setCurrentStep(currentStep);
    } catch (error: any) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const reset = async () => {
    setLoading(true);
    const currentStep = await camundaApi.reset();
    setCurrentStep(currentStep);

    const definitionName = currentConfig?.definitionName;
    const domain: string = `${currentConfig?.brandId}.${currentConfig?.domain}`;

    trackPageViewEvents({
      stepTitle: "process-started",
      definitionName,
      domain,
      sendAmplitudeEvent: false,
    });

    trackCCStartedEvent(undefined);
    setError(null);
    setLoading(false);
  };

  const refreshStep = async () => {
    setLoading(true);

    const currentStep = await camundaApi.refreshStep();
    setCurrentStep(currentStep);

    setError(null);
    setLoading(false);
  };

  const flush = async () => {
    setLoading(true);
    return await camundaApi.flush();
  };

  const syncSignature = async (process: string) => {
    setLoading(true);
    await camundaApi.syncSignature(process);
    setLoading(false);
  };

  const executeAction = async (
    content: ActionContent,
    process: string,
    utm_campaign?: string
  ) => {
    setLoading(true);

    try {
      let response = await content.action(process, utm_campaign);

      if (!response.ok) {
        setCurrentStep({
          components: [content.errorField],
          extensionProperties: {
            displayStepTitle: content.displayErrorStepTitle,
          },
        });
      } else {
        setCurrentStep({
          components: [content.field],
          extensionProperties: { displayStepTitle: content.displayStepTitle },
        });
        setError(null);
      }
    } catch (error: any) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onPopState = () => {
      const hasCancelBtn = !!document.querySelector(
        '[data-cy="cancel-step"], [data-nav="cancel-step"]'
      );

      if (
        hasCancelBtn &&
        visitedSteps.length > 1 &&
        window.location.hash === `#${visitedSteps[visitedSteps.length - 2]}`
      ) {
        goToPreviousStep();
      } else {
        window.location.hash = visitedSteps[visitedSteps.length - 1];
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [currentStep]);

  const waitForConsentAndPush = () => {
    if (
      Array.isArray(window.dataLayer) &&
      window.dataLayer.some((item) => item.event === "cookie_consent_update") &&
      window.amplitude
    ) {
      const device = new DeviceDetector().parse(navigator.userAgent);
      const deviceCategory = device.device?.type || "unknown";
      const browserCategory = device.client?.name || "unknown";

      const identify = new window.amplitude.Identify();
      identify.set("Device Category", deviceCategory);
      identify.set("Browser Category", browserCategory);
      window.amplitude?.identify(identify);
    } else {
      setTimeout(waitForConsentAndPush, 1000);
    }
  };

  return {
    currentStep,
    isLoading,
    error,
    visitedSteps,
    initialise,
    submitStep,
    goToPreviousStep,
    refreshStep,
    reset,
    flush,
    executeAction,
    syncSignature,
  };
};
